import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, ListItem, Typography } from '@mui/material';
import {
    getAllModifierProductsForRootReferenceId,
    IItemReadResourceV10
} from 'components/basket/model/Basket';
import {
    getModifierProductsTotal,
    getPayProductOptionsDetails,
    ProductModifierPayItem
} from 'components/menu/model/Menu';
import { addOpacity } from 'lib/helpers';
import { useCurrencyString } from 'lib/useCurrencyString';
import { constructBasketItemsTree } from 'components/basket/helpers';
import { PayRecursiveModifierProductList } from './PayRecursiveModifierProducts';

const PREFIX = 'PayOrderItem';

const classes = {
    productWrapper: `${PREFIX}productWrapper`,
    productQuantity: `${PREFIX}productQuantity`,
    productInfo: `${PREFIX}productInfo`,
    modifier: `${PREFIX}modifier`,
    quantity: `${PREFIX}quantity`,
    productPrice: `${PREFIX}productPrice`,
    modifierQuantity: `${PREFIX}modifierQuantity`
};

const StyledListItem = styled(ListItem)(({ theme }) => ({
    width: '100%',
    flexDirection: 'column',
    padding: theme.spacing(0.5, 0),
    alignItems: 'flex-start',
    '& .MuiTypography-root': {
        fontSize: '14px'
    },
    [`& .${classes.productWrapper}`]: {
        display: 'flex',
        width: '100%',
        flexShrink: 0
    },

    [`& .${classes.productQuantity}`]: {
        minWidth: '24px'
    },

    [`& .${classes.productInfo}`]: {
        flex: 1
    },

    [`& .${classes.modifier}`]: {
        color: addOpacity(theme.palette.text.primary, 0.6),
        whiteSpace: 'pre-wrap'
    },

    [`& .${classes.quantity}`]: {
        color: addOpacity(theme.palette.text.primary, 0.4),
        paddingRight: 0
    },

    [`& .${classes.productPrice}`]: {
        minWidth: '64px',
        textAlign: 'right'
    },

    [`& .${classes.modifierQuantity}`]: {
        display: 'inline-block'
    }
}));

const StyledUL = styled('ul')(({ theme }) => ({
    paddingLeft: theme.spacing(3.5),
    listStyle: 'none',
    '& li:before': { content: '"- "', color: addOpacity(theme.palette.text.primary, 0.6) },
    '& li': {
        textIndent: '-5px'
    }
}));

interface PayOrderItemProps {
    item: IItemReadResourceV10;
    allItems: IItemReadResourceV10[];
    onClick?: (item: IItemReadResourceV10) => void;
    secondary?: boolean;
}

export const PayOrderItem: React.FC<React.PropsWithChildren<PayOrderItemProps>> = ({
    item,
    allItems,
    onClick,
    secondary
}) => {
    const getCurrencyString = useCurrencyString();

    const modifierProducts = React.useMemo(
        () => getAllModifierProductsForRootReferenceId(allItems, item.referenceId ?? ''),
        [allItems, item.referenceId]
    );
    const modifierProductsTotal = React.useMemo(
        () => getModifierProductsTotal(modifierProducts, item.quantity) + item.cost,
        [modifierProducts, item.quantity, item.cost]
    );

    const [modifierOptions, calculatedOptionsPrice] = React.useMemo(
        () => getPayProductOptionsDetails(item.modifiers),
        [item.modifiers]
    );

    const totalPrice = React.useMemo(
        () => Math.round((modifierProductsTotal + calculatedOptionsPrice) * item.quantity * 100) / 100,
        [modifierProductsTotal, calculatedOptionsPrice, item.quantity]
    );

    const nestedProductModifiers = React.useMemo(() => {
        const mapped = constructBasketItemsTree([item, ...modifierProducts]);
        return mapped[0].children || [];
    }, [item, modifierProducts]);

    const handleClick = React.useCallback(() => {
        if (onClick) {
            onClick(item);
        }
    }, [item, onClick]);

    const renderOptionModifier = React.useCallback(
        (optionModifier: ProductModifierPayItem, index: number) => (
            <li key={`product-modifier-${index}`}>
                <Typography className={classes.modifier} display="inline">
                    <span className={classes.modifierQuantity}>
                        {optionModifier.quantity > 1 ? `${optionModifier.quantity}x` : ''}
                    </span>
                    {optionModifier.title}
                    {optionModifier.price ? ` (${getCurrencyString(optionModifier.price)})` : ''}
                </Typography>
            </li>
        ),
        [getCurrencyString]
    );

    if (item.parentReferenceId) {
        return null;
    }
    return (
        <StyledListItem disableGutters onClick={handleClick} sx={{ opacity: secondary ? 0.5 : 1 }}>
            <Box className={classes.productWrapper}>
                <Typography className={classes.productQuantity}>
                    {item.quantity}
                    {'x'}
                </Typography>
                <Box className={classes.productInfo}>
                    <Typography>{item.productName}</Typography>
                </Box>
                {item.quantity > 1 && (
                    <Typography className={classes.quantity}>
                        {getCurrencyString(modifierProductsTotal)}
                    </Typography>
                )}
                <Typography className={classes.productPrice}>{getCurrencyString(totalPrice)}</Typography>
            </Box>
            <StyledUL>{modifierOptions.map(renderOptionModifier)}</StyledUL>
            {nestedProductModifiers.length > 0 && (
                <Box sx={{ paddingLeft: 2.5 }}>
                    <PayRecursiveModifierProductList
                        modifierProducts={nestedProductModifiers}
                        orderItemQuantity={item.quantity}
                        typographyVariant="caption"
                    />
                </Box>
            )}
        </StyledListItem>
    );
};
